interface RequiredField {
  name: string;
  required: boolean;
}

interface UiBehaviorRule {
  name: string;
  enabled: boolean;
}

/**
 * Constants for ERP required field names.
 * These string values must match what the backend sends in the requiredFields array.
 */
export const REQUIRED_FIELD_NAMES = {
  VOLUME: 'EstimatedVolume',
  CALLER: 'Caller'
} as const;

/**
 * Constants for ERP UI behavior rule names.
 * These string values must match what the backend sends in the uiBehaviorRules array.
 */
export const UI_BEHAVIOR_RULES = {
  RESET_STATUS_ON_DATE_CHANGE: 'ResetJobStatusOnJobDateChange',
  REMOVE_UNIT_CREW_ON_DATE_CHANGE: 'RemoveUnitAndCrewOnJobDateChange',
  REMOVE_CREW_ON_UNIT_CHANGE: 'RemoveCrewOnEquipmentUnitChange',
  CLEAR_NOTIFIED_ACKNOWLEDGED_ON_DATE_CHANGE: 'ClearNotifiedAndAcknowledgedOnJobDateChange',
  CLEAR_NOTIFIED_ON_TIME_CHANGE: 'ClearNotifiedOnJobTimeChange',
  COD_PRE_SELECTED: 'CODPreSelected'
} as const;

/**
 * Check if a specific field is marked as required in the ERP settings.
 *
 * @param fields - Array of required field definitions from ERP
 * @param fieldName - The name of the field to check
 * @returns true if the field is required, false otherwise
 */
export function isFieldRequired(fields: RequiredField[], fieldName: string): boolean {
  if (!fields || fields.length === 0) return false;
  const field = fields.find((f) => f.name === fieldName);
  return field?.required === true;
}

/**
 * Check if a specific UI behavior rule is enabled in the ERP settings.
 *
 * @param rules - Array of UI behavior rule definitions from ERP
 * @param ruleName - The name of the rule to check
 * @returns true if the rule is enabled, false otherwise
 */
export function isRuleEnabled(rules: UiBehaviorRule[], ruleName: string): boolean {
  if (!rules || rules.length === 0) return false;
  const rule = rules.find((r) => r.name === ruleName);
  return rule?.enabled === true;
}

/**
 * Generic utility to extract the default item's ID from an ERP options list.
 * Returns the ID of the item with default=true, or null if no default is marked.
 *
 * @param items - Array of items with id (nullable) and optional default flag
 * @returns The ID of the default item, or null if none found or if default item has null id
 */
export function getDefaultId<T extends { id?: number | null; default?: boolean | null }>(items: T[]): number | null {
  if (!items.length) return null;
  const defaultItem = items.find((item) => item.default === true);
  return defaultItem?.id ?? null;
}
